import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    /* 30/09/2026 Piacersi — IL CONTENITORE SI LEGGE DALLA PAGINA A OGNI CAMBIO.
       `this.refs` lo tiene aggiornato un MutationObserver che Component attacca
       solo in requestIdleCallback (component.js): su un telefono lento arriva
       fino a 12 s dopo l'apertura. Se il primo cambio di formato risponde
       prima, `replaceWith` qui sotto stacca il nodo, il ref resta sul nodo
       staccato e da li' in poi il prezzo non cambia piu', mentre il carrello
       riceve la variante giusta (Hairgenie Silky Liss 250/500 ml, 3 volte su 4
       a CPU 6x). Una querySelector per cambio non costa niente; i refs restano
       come ripiego, e si riallineano qui sotto dopo ogni sostituzione. */
    const refs = /** @type {any} */ (this.refs);
    const priceContainer = this.#trova('priceContainer');
    const volumePricingNote = this.#trova('volumePricingNote');
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
      refs.priceContainer = newPrice;
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
      delete refs.volumePricingNote;
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      const copia = /** @type {Element} */ (newNote.cloneNode(true));
      (newPrice ?? priceContainer)?.insertAdjacentElement('afterend', copia);
      refs.volumePricingNote = copia;
    } else {
      volumePricingNote.replaceWith(newNote);
      refs.volumePricingNote = newNote;
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /**
   * Il nodo col ref dato come sta ADESSO nella pagina; il ref salvato solo se
   * e' ancora attaccato (vedi il commento in updatePrice).
   * @param {'priceContainer' | 'volumePricingNote'} nome
   * @returns {Element | undefined}
   */
  #trova(nome) {
    const inPagina = this.querySelector(`[ref="${nome}"]`);
    if (inPagina) return inPagina;
    const salvato = /** @type {any} */ (this.refs)[nome];
    return salvato instanceof Element && salvato.isConnected ? salvato : undefined;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
